import { useState, useEffect } from 'react';

const PHONE_REGEX = /^\+[1-9]\d{1,14}$/;

/**
 * Custom hook to manage EnterPhoneNumber dialog state and logic.
 * @param {boolean} open - Whether the dialog is open
 * @param {Function} onCancel - Callback when cancel is triggered
 * @param {Function} onVerify - Callback with the phone number when verify is triggered
 * @returns {Object} State and handlers for the phone number dialog
 */
export const useEnterPhoneNumber = (open, onCancel, onVerify) => {
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const phoneValue = phone ?? '';
  const isValid = PHONE_REGEX.test(phoneValue);
  const hasError = touched && !isValid && phoneValue.length > 0;

  const fieldState = !touched || phone === ''
    ? 'default'
    : hasError
    ? 'error'
    : 'success';

  const reset = () => {
    setPhone('');
    setTouched(false);
    setIsLoading(false);
  };

  const handleChange = (e) => {
    setPhone(e.target.value);
    if (!touched) setTouched(true);
  };

  const handleBlur = () => setTouched(true);

  const handleCancel = () => {
    reset();
    if (onCancel) onCancel();
  };

  const handleVerify = async () => {
    setTouched(true);
    if (!isValid || !onVerify) return;
    setIsLoading(true);
    try {
      await onVerify(phone);
    } finally {
      setIsLoading(false);
    }
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) reset();
  }, [open]);

  return {
    phone,
    isValid,
    isLoading,
    fieldState,
    handleChange,
    handleBlur,
    handleCancel,
    handleVerify,
  };
};

export default useEnterPhoneNumber;
